import type {
  OpenLoan,
  PayoffLetterResponse,
  SettlementLetterRequest,
  VehicleInfoRequest,
} from "@credit-acceptance/step6-bff-client";
import { useMutation } from "@tanstack/react-query";

import { payoffApi } from "../../../api/client";
import { buildCommonLetterRequestFields, type PayoffLetterInput } from "./useGeneratePayoffLetter";

/**
 * Maps an `OpenLoan`'s vehicle info to the request-side shape, stringifying
 * `year` (a number in the check response, a string in the letter request).
 * @param {OpenLoan} loan - The selected open-loan entry.
 * @returns {VehicleInfoRequest} The mapped vehicle info.
 */
function buildVehicleInfoRequest(loan: OpenLoan): VehicleInfoRequest {
  return {
    make: loan.vehicleInfo?.make,
    model: loan.vehicleInfo?.model,
    year: loan.vehicleInfo?.year === undefined ? undefined : String(loan.vehicleInfo.year),
    vin: loan.vehicleInfo?.vin,
  };
}

/**
 * Maps a selected `OpenLoan` entry plus the analyst-entered date/mileage
 * override to a `SettlementLetterRequest`. `hasGap`/`hasVsc` rename from the
 * loan's `isHasGap`/`isHasVsc`; `isGapThirdParty`/`isVscThirdParty` keep
 * their name. `vehicleInfo.year` is a number on the loan but a string here.
 * `vehicleInfo`, `payoffAmount`, `settlementAmount`, and `contractDate` are
 * required by the request but optional on the loan; `isSettlementLetterAllowed`
 * is BFF-authoritative and guarantees they're populated whenever this is
 * called for real, same as `isBankVerificationAllowed` for bank verification.
 * @param {OpenLoan} loan - The selected open-loan entry.
 * @param {PayoffLetterInput} input - The analyst-entered payoff date/mileage override.
 * @returns {SettlementLetterRequest} The mapped request.
 */
export function buildSettlementLetterRequest(loan: OpenLoan, input: PayoffLetterInput): SettlementLetterRequest {
  return {
    ...buildCommonLetterRequestFields(loan, input),
    vehicleInfo: buildVehicleInfoRequest(loan),
    payoffAmount: loan.payOffQuote?.payOffAmount ?? 0,
    settlementAmount: loan.payOffQuote?.settlementAmount ?? 0,
    firstName: loan.firstName,
    lastName: loan.lastName,
    dealerStateCode: loan.dealerStateCode,
    contractDate: loan.contractDate ?? "",
  };
}

/**
 * Generates a settlement letter for a loan. Never caches or dedups the
 * returned `documentId` — every click issues a fresh request (caps2 parity).
 * @returns {ReturnType<typeof useMutation<PayoffLetterResponse, unknown, SettlementLetterRequest>>} The mutation.
 */
export function useGenerateSettlementLetter() {
  return useMutation({
    mutationFn: async (request: SettlementLetterRequest): Promise<PayoffLetterResponse> => {
      const response = await payoffApi.generateSettlementLetter(request);
      return response.data;
    },
  });
}
